import { useQueryClient } from '@tanstack/react-query';
import { MessageCircleIcon, MessagesSquareIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { useCurrentUser } from '@/shared/auth';
import { Avatar } from '@/shared/components/avatar';
import { EmptyState } from '@/shared/components/empty-state';
import { ListSkeleton } from '@/shared/components/skeletons';
import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { useRealtimeEvent } from '@/shared/realtime';
import { CounterBadge } from '@/shared/ui/badge';
import { buttonVariants } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { messengerKeys, useChats, useUnread } from '../api';
import { CallLayer } from '../calls/CallLayer';
import { callManager, type CallEndReason } from '../calls/callManager';
import { formatDuration, listTime, previewText } from '../lib/format';
import { chatTitle } from '../lib/titles';
import { MessengerEvents } from '../realtime';
import { useMessengerStore } from '../store';
import type { ChatListItem, Message } from '../types';

import { ChatAvatar } from './ChatAvatar';

/**
 * Top bar entry of the messenger: unread counter, a popover with recent chats, toasts for messages arriving outside
 * the open chat, and the app-wide call layer (incoming calls, the call window).
 */
export function MessengerTopBarButton() {
  const { t } = useTranslation('messenger');
  const unread = useUnread();
  const [open, setOpen] = useState(false);
  const count = unread.data?.messages ?? 0;
  const label = count > 0 ? t('widget.labelUnread', { count }) : t('widget.label');

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <SimpleTooltip content={t('widget.label')}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={label}
              data-testid="messenger-widget"
              data-unread={count}
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'icon' }),
                'relative text-muted-foreground',
              )}
            >
              <MessageCircleIcon className="size-[1.125rem]" />
              <CounterBadge value={count} className="absolute -top-0.5 -right-0.5 ring-2 ring-surface" />
            </button>
          </PopoverTrigger>
        </SimpleTooltip>
        <PopoverContent align="end" className="w-[min(22rem,calc(100vw-1.5rem))] p-0">
          {open && <RecentChats onNavigate={() => setOpen(false)} />}
        </PopoverContent>
      </Popover>
      <MessageToasts />
      <CallEvents />
      <CallLayer />
    </>
  );
}

function RecentChats({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation('messenger');
  const chats = useChats('');
  const me = useCurrentUser();
  const fmt = useFormatters();
  const items = (chats.data ?? []).slice(0, 7);
  return (
    <div>
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <p className="text-sm font-semibold">{t('widget.recent')}</p>
        <Link
          to="/messenger"
          onClick={onNavigate}
          className="text-xs font-medium text-brand-text hover:underline"
        >
          {t('widget.openAll')}
        </Link>
      </div>
      {chats.isLoading ? (
        <ListSkeleton rows={4} className="px-4" />
      ) : items.length === 0 ? (
        <EmptyState size="sm" icon={MessagesSquareIcon} title={t('list.empty')} />
      ) : (
        <ul className="max-h-96 overflow-y-auto p-1.5">
          {items.map((chat) => (
            <li key={chat.id}>
              <Link
                to={`/messenger/${chat.id}`}
                onClick={onNavigate}
                className="flex items-center gap-3 rounded-lg px-2 py-2 focus-ring hover:bg-accent"
              >
                <ChatAvatar chat={chat} size="md" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        'truncate text-sm',
                        chat.unreadCount > 0 ? 'font-semibold' : 'font-medium',
                      )}
                    >
                      {chatTitle(chat, t)}
                    </span>
                    <span className="ml-auto shrink-0 text-[0.6875rem] text-subtle-foreground tabular-nums">
                      {listTime(chat.lastMessage?.createdAt ?? chat.lastActivityAt, fmt, {
                        yesterday: t('time.yesterday'),
                      })}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="line-clamp-1 min-w-0 flex-1 text-xs break-all text-muted-foreground">
                      {chat.lastMessage
                        ? previewText(
                            chat.lastMessage,
                            { me: me.id, isGroup: chat.type === 'group', isSaved: chat.type === 'saved' },
                            t,
                          )
                        : ''}
                    </span>
                    <CounterBadge
                      value={chat.unreadCount}
                      className={cn('shrink-0', chat.isMuted && 'bg-subtle-foreground')}
                    />
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A toast for a message from someone else outside the open chat (muted chats stay silent). */
function MessageToasts() {
  const { t } = useTranslation('messenger');
  const me = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useRealtimeEvent<Message>(MessengerEvents.messageCreated, (message) => {
    if (!message.author || message.author.id === me.id || message.kind !== 'text') return;
    if (useMessengerStore.getState().openChatId === message.chatId && document.visibilityState === 'visible')
      return;
    const chats = queryClient.getQueryData<ChatListItem[]>(messengerKeys.chatList(''));
    const chat = chats?.find((c) => c.id === message.chatId);
    if (chat?.isMuted && !message.mentionedUserIds.includes(me.id)) return;
    const groupTitle = chat?.type === 'group' ? chat.title : null;
    const author = message.author;
    toast.custom(
      (id) => (
        <button
          type="button"
          onClick={() => {
            toast.dismiss(id);
            void navigate(`/messenger/${message.chatId}`);
          }}
          className="flex w-[min(22rem,calc(100vw-2rem))] items-start gap-3 rounded-xl border bg-popover p-3 text-left text-popover-foreground shadow-lg focus-ring"
          data-testid="message-toast"
        >
          <Avatar user={author} size="lg" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{author.fullName}</span>
            {groupTitle && <span className="block truncate text-xs text-brand-text">{groupTitle}</span>}
            <span className="mt-0.5 line-clamp-2 text-[0.8125rem] break-words text-muted-foreground">
              {message.text || `📎 ${t('message.attachment')}`}
            </span>
          </span>
        </button>
      ),
      { id: `message-${message.chatId}`, duration: 5000 },
    );
  });
  return null;
}

/** Toasts for the call manager (ended calls, media and signaling problems). */
function CallEvents() {
  const { t } = useTranslation('messenger');
  useEffect(() => {
    callManager.events = {
      onEnded: (ended: CallEndReason) => {
        if (ended.outcome === 'replaced') toast.info(t('call.replaced'));
        else if (ended.outcome === 'completed' && 'durationSeconds' in ended)
          toast(t('call.endedToast', { duration: formatDuration(ended.durationSeconds) }));
        else if (ended.outcome === 'declined') toast(t('call.declinedToast'));
        else if (ended.outcome === 'missed') toast(t('call.missedToast'));
      },
      onMediaError: (kind) => toast.warning(t(`call.mediaError.${kind}`)),
      onError: (code) =>
        toast.error(t(`errors.${code.replace(/^messenger\./u, '')}`, { defaultValue: t('call.failed') })),
    };
    return () => {
      callManager.events = {};
    };
  }, [t]);
  return null;
}
